import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@apollo/client';
import { AdsChunkSize } from '../AdsWithInfiniteScroll.types';
import * as S from './PremiumAdsWithInfiniteScroll.styles';
import { GetAdsResponse, ServiceName } from '@/api/types/ads.types';
import { GET_ADS } from '@/api/queries/ads.queries';
import { mergeAdsResults } from '@/utils/helpers';
import { usePlatformSource } from '@/hooks/usePlatformSource';
import { ROUTES } from '@/constants/routes';
import { useServerDataContext } from '@/contexts';
import { PremiumAdsGrid } from '@/components/AdsWithInfiniteScroll/PremiumAdsWithInfiniteScroll/PremiumAdsGrid';
import { InfiniteLoader } from '@/components/InfiniteLoader/InfiniteLoader';
import { useResetScrollPosition } from '@/hooks/useResetScrollPosition';

export const PremiumAdsWithInfiniteScroll = () => {
  const { t } = useTranslation();
  const source = usePlatformSource();
  const [isLoadingMore, setLoadingMore] = useState(false);
  const { initialPremiumAds } = useServerDataContext();

  const { data, loading, fetchMore } = useQuery<GetAdsResponse>(
    GET_ADS(ServiceName.Premium),
    {
      variables: {
        adKind: ServiceName.Premium,
        first: AdsChunkSize.Middle,
        source,
      },
      notifyOnNetworkStatusChange: true,
    }
  );

  const ads = data?.ads?.nodes ?? initialPremiumAds?.ads?.nodes;
  const { endCursor, hasNextPage } = data?.ads?.pageInfo || {};

  useResetScrollPosition(!ads);

  function onLoadMore() {
    if (loading || isLoadingMore || !hasNextPage || !navigator.onLine) return;

    setLoadingMore(true);
    fetchMore({
      variables: {
        after: endCursor,
      },
      updateQuery: (prevResult, { fetchMoreResult }) =>
        mergeAdsResults(prevResult, fetchMoreResult, {
          shouldFilterDuplicates: true,
        }),
    }).finally(() => setLoadingMore(false));
  }

  return (
    <S.Box>
      <S.Head>
        <S.Title>{t('pages.home.premium_ads')}</S.Title>
        <S.LatestAdsButton href={ROUTES.ad}>
          {t('pages.ads.latest_ads')}
        </S.LatestAdsButton>
      </S.Head>

      <PremiumAdsGrid ads={ads} isLoading={loading} />
      <InfiniteLoader onVisit={onLoadMore} rootMargin={1000} />
    </S.Box>
  );
};

PremiumAdsWithInfiniteScroll.displayName = 'PremiumAdsWithInfiniteScroll';
